import React, { useEffect, useState } from 'react';
import { ChildProps } from 'react-apollo';
import gql from 'graphql-tag';
import ListingNudge from '@reverbdotcom/discovery-ui/src/components/discovery/listing_nudge';
import { CartSignals as CartSignalsQuery } from '../schemas/rql';

import { IUserContext, withUserContext } from '@reverbdotcom/commons';
import { trackEvent, MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import experiments from '@reverbdotcom/commons/src/experiments';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';

interface ExternalProps {
  listingId: string;
  skipLoadingSignals?: boolean; // temporary prop for experiment
  skipShowingSignals?: boolean; // temporary prop for experiment
  shouldTrack?: boolean // temporary prop for experiment; because we show the component in two places, we have to load it twice... which normally would mean tracking it twice. So, as a hack, we can pass shouldTrack=false to the second instance of the component on every load.
}

interface Signal {
  name?: string,
  title?: string,
  icon?: string
}

type IProps = ChildProps<ExternalProps & IUserContext, CartSignalsQuery.Query, Location>;

export function CartSignals(
  props: IProps,
) {

  const signals = props.data?.listing?.signals;
  TrackCartItemSignalsView(props, signals);
  if (!signals?.length || props.skipShowingSignals) { return null; }

  return (
    <>
      {signals.map(signal => {
        return RenderSignal(signal);
      })}
    </>
  );
}

function RenderSignal(signal: Signal) {
  if (!signal.title || !signal.icon) {
    return null;
  }

  const getIconSvg = function () {
    const iconHeight = 20;
    return (
      <svg height={iconHeight}>
        <image href={signal.icon} height={iconHeight} />
      </svg>
    );
  };

  return (
    <ListingNudge
      title={signal.title}
      inline
      icon={getIconSvg()}
      key={signal.title}
    />
  );
}

const withQuery = withGraphql<IProps, CartSignalsQuery.Query>(
  gql`
    query Cart_Signals(
      $id: String
      $skipLoadingSignals: Boolean!
    ){
      listing(
        input: {id: $id}
      ) {
        _id
        id
        signals (input: { groups: [ CART_PAGE_WHY_TO_BUY ] }) @skip(if: $skipLoadingSignals) {
          name
          title
          icon
        }
      }
    }
  `,
  {
    options: (ownProps) => {
      return {
        errorPolicy: 'all',
        variables: {
          id: ownProps.listingId,
          skipLoadingSignals: ownProps.skipLoadingSignals || false,
        },
      };
    },
  },
);

function TrackCartItemSignalsView(props: IProps, signals: Signal[]) {
  const [hasViewed, setViewed] = useState(false);

  useEffect(() => {
    if (hasViewed || !signals?.length || !props.shouldTrack) return;

    const customTrackedValues = {
      signals: signals?.map(signal => signal.name)?.join('|') || '',
    };

    trackEvent({
      eventName: MParticleEventName.CartItemSignalsViewed,
      listingId: props.data.listing.id,
      experiments: formatExperimentsForEventAttributes(props.user, [experiments.CART_SIGNALS_MOBILE]),
      ...customTrackedValues,
    });

    setViewed(true);
  }, [props, hasViewed]);
}

export default withUserContext(withQuery(CartSignals));
